import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * What this visitor is plainly looking for, for THIS VISIT ONLY.
 *
 * WHY THIS IS NOT A TRACKER, which is the whole design and not a caveat.
 *
 * Iceberg's Neuron sells a website that "adapts to each visitor in real
 * time... 1 of 1, not 1 of 3". The obvious way to build that is to remember
 * the visitor between visits, and the obvious way to do THAT is a visitor id
 * in local storage. It is also the one thing Foundation has promised not to
 * do. The cookie policy on every agency site says the pageview counter
 * "stores no cookie, no visitor ID, and can't be used to identify or track an
 * individual visitor", and that "because we don't set optional cookies on
 * this site, there's nothing to opt out of here".
 *
 * Honouring that is not only a legal convenience. Each agency is the data
 * controller for its own website, and switching on cross-visit tracking for
 * all of them, on their behalf, without any of them asking, is not a
 * decision a platform gets to make quietly on a Sunday.
 *
 * So nothing here is stored. Not a cookie, not localStorage, not
 * sessionStorage. It is React state, it lives as long as the tab is on the
 * site, and a refresh wipes it. There is no identifier, nothing leaves the
 * browser, and the server is never told. That means:
 *
 *   - A FIRST-TIME VISITOR sees exactly the page everyone has always seen.
 *   - EVERY CRAWLER sees exactly that page too, because the prerenderer
 *     serves it and nothing here runs server-side. Adapting what a bot is
 *     shown would be cloaking; this cannot, by construction.
 *   - There is nothing to consent to, because nothing is kept.
 *
 * What is left is still most of what the competitor is selling: somebody who
 * has just read the Windlesham area guide and clicks back to the home page
 * should be shown Windlesham houses, not a national selection. The site
 * answers what they came for, within the visit where they said it.
 *
 * And it SAYS SO. Every adapted band carries a line explaining why it looks
 * like that and a control to drop it, because a page that quietly rearranges
 * itself is unsettling, and the honest version of personalisation tells you
 * it is happening.
 */

const EMPTY = { areas: [], valued: false, registered: false };

const VisitInterestContext = createContext({
  ...EMPTY,
  topArea: null,
  noteArea: () => {},
  noteValued: () => {},
  noteRegistered: () => {},
  forget: () => {},
});

/** Lower-cased, hyphenated, so "Virginia Water" and the /area/ slug meet. */
export function areaKey(text) {
  return String(text || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function VisitInterestProvider({ children }) {
  const [state, setState] = useState(EMPTY);

  const noteArea = useCallback((name) => {
    const key = areaKey(name);
    if (!key) return;
    setState((s) => {
      // Most recent first, and deliberately short: this is "what are they
      // looking at", not a profile.
      const areas = [{ key, label: String(name).trim() },
                     ...s.areas.filter((a) => a.key !== key)].slice(0, 3);
      return { ...s, areas };
    });
  }, []);

  const noteValued = useCallback(() => setState((s) => ({ ...s, valued: true })), []);
  const noteRegistered = useCallback(() => setState((s) => ({ ...s, registered: true })), []);
  const forget = useCallback(() => setState(EMPTY), []);

  const value = useMemo(() => ({
    ...state,
    topArea: state.areas[0] || null,
    noteArea, noteValued, noteRegistered, forget,
  }), [state, noteArea, noteValued, noteRegistered, forget]);

  return (
    <VisitInterestContext.Provider value={value}>{children}</VisitInterestContext.Provider>
  );
}

export function useVisitInterest() {
  return useContext(VisitInterestContext);
}

/**
 * Put the houses they have been reading about first.
 *
 * A REORDER AND NEVER A FILTER. Hiding the rest would make the page lie
 * about what the agency has on, and an agency with four houses would look
 * like an agency with one. Everything stays; the relevant ones come first.
 */
export function leadWithArea(rows, key) {
  if (!key || !Array.isArray(rows) || rows.length < 2) return { rows, moved: 0 };
  const hit = (p) => areaKey(p.town) === key
    || areaKey(p.area) === key
    || areaKey(p.display_address).includes(key);
  const first = rows.filter(hit);
  if (!first.length || first.length === rows.length) return { rows, moved: 0 };
  return { rows: [...first, ...rows.filter((p) => !hit(p))], moved: first.length };
}
